import { useEffect } from "react";
import "./App.css";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import StudioLayout from "./StudioLayout";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import Dashboard from "./routes/Dashboard";
import Events from "./routes/Events";
import Features from "./routes/Features";
import Login from "./routes/Login";
import MessageRendered from "./routes/MessageRendered";
import NotFound from "./routes/NotFound";
import OAuthVerification from "./routes/OAuthVerification";
import TeamInternal from "./routes/TeamInternal";
import Teams from "./routes/Teams";
import Templates from "./routes/Templates";
import UserInternal from "./routes/UserInternal";
import Users from "./routes/Users";
import { GlobalStateProvider } from "./state";
import FileInternal from "./routes/FileInternal";

function RequireAuth({ component }: { component: JSX.Element }) {
  let auth = useAuth();
  let location = useLocation();

  if (!auth.isAuthenticated) {
    console.log(
      `App: authenticated: ${auth.isAuthenticated}, location: ${location.pathname}`
    );
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return component;
}

function App() {
  const [searchParams] = useSearchParams();
  const reset = searchParams.get("reset");

  // TODO: remove in prod
  useEffect(() => {
    if (reset !== null) {
      localStorage.clear();
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <GlobalStateProvider>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<StudioLayout />}>
              {/* public  */}
              <Route path="/login" element={<Login />} />
              <Route
                path="/oauth/:providerId"
                element={<OAuthVerification />}
              />
              {/* private  */}
              <Route
                path="/"
                element={<RequireAuth component={<Dashboard />} />}
              />
              <Route
                path="/users"
                element={
                  <RequireAuth
                    component={<RequireAuth component={<Users />} />}
                  />
                }
              />
              <Route
                path="/user/:userId"
                element={<RequireAuth component={<UserInternal />} />}
              />
              <Route
                path="/file/:fileId"
                element={<RequireAuth component={<FileInternal />} />}
              />
              <Route
                path="/teams"
                element={
                  <RequireAuth
                    component={<RequireAuth component={<Teams />} />}
                  />
                }
              />
              <Route
                path="/team/:teamId"
                element={<RequireAuth component={<TeamInternal />} />}
              />
              <Route
                path="/events"
                element={<RequireAuth component={<Events />} />}
              />
              <Route
                path="/templates"
                element={<RequireAuth component={<Templates />} />}
              />
              <Route
                path="/features"
                element={<RequireAuth component={<Features />} />}
              />
              <Route
                path="/message/:messageId/render"
                element={<RequireAuth component={<MessageRendered />} />}
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </AuthProvider>
      </GlobalStateProvider>
    </>
  );
}

export default App;
